import {
  FunnelIcon,
  CalendarIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { useLocalStorageState } from 'ahooks';
import { useEffect, useState } from 'react';
import { Button, Divider, Dropdown, Radio, Toggle } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import {
  Button as DsButton,
  ButtonGroup as DsButtonGroup,
  Select as DsSelect,
} from '@leadsales/design-system/react';
import ActiveFilterBadge from '../ActiveFilterBadge/ActiveFilterBadge.component';
import {
  ALL_FILTER,
  canFilterByStage,
  funnelFilterOptions,
  stageFilterOptions,
} from '../../pages/Directory/Directory.funnelFilter.ts';
import { PipeService } from '../../pages/Pipe/Pipe.service.ts';
import { tTagFilter } from '../../pages/Pipe/Pipe.types.ts';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context.ts';
import { eDateFilterType } from '../../providers/ModalsProvider/Modals.types.ts';
import {
  eChannelType,
  eLeadStatus,
} from '../../providers/SocketsProvider/Sockets.enums.ts';
import { useUserContext } from '../../providers/UserProvider/User.context.ts';
import { eUserRole } from '../../providers/UserProvider/User.enums.ts';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context.ts';
import { showStatusEmoji } from '../Chat/Chat.functions.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context.ts';
import { VITE_TIKTOK_FEATURE } from 'src/constants/envs.ts';
import './LeadFilters.styles.scss';

export type tLeadFiltersProps = {
  /**
   * Whether the advanced-filters control is offered alongside search. Search
   * alone is valid on surfaces with nothing to filter.
   */
  showFilters: boolean;
  /**
   * Whether to draw the search button. The two halves split across surfaces on
   * the funnel: search belongs to the navbar (it is global and reaches the same
   * modal from every route), while the advanced filters belong to the board
   * they filter. Defaults to true so every other mount is unchanged.
   */
  showSearch?: boolean;
  /**
   * Whether to draw the Filtros button (and its clear ×) that opens the panel.
   * Directory v2 opens the panel from its own table bar, where Filtros also
   * shows how many filters are on and clears them, so the navbar leaves its
   * button out there. The panel itself still renders from this mount: it has
   * one home, whoever opens it. Defaults to true so every other mount is
   * unchanged.
   */
  showFilterButton?: boolean;
  /**
   * RET-2331: Embudo and Etapa at the top of the panel, the same filter the
   * new Directory's column headers set. Only the new Directory passes it: the
   * funnel board filters a funnel it already shows, and the previous
   * Directory has its own headers. Defaults to false.
   */
  showDirectoryFunnelFilter?: boolean;
  /**
   * RET-2331: under Etiquetas, whether a lead needs any of the chosen tags or
   * all of them. Only the new Directory passes it, and the choice shows once
   * two or more tags are chosen. Defaults to false.
   */
  showDirectoryTagsMatch?: boolean;
  /**
   * INT-1596: whether the panel mirrors the funnel board's "No entregados"
   * chip. Only the board passes it, and only while the chip itself shows, so
   * the two surfaces of that one state never disagree. Defaults to false.
   */
  showUndeliveredFilter?: boolean;
  /** INT-1602: called when the panel's switch turns the filter on. */
  onUndeliveredFilterOn?: () => void;
};

/**
 * Lead search + advanced filters.
 *
 * Lifted out of the Navbar so it can render in two places: the funnel's own
 * action bar, and the shared navbar for Directory. All filter state already
 * lives in ModalsProvider, so both mounts drive the same filters — this moves
 * only where the controls are drawn, never who owns the state.
 */
export default function LeadFilters({
  showFilters,
  showSearch = true,
  showFilterButton = true,
  showDirectoryFunnelFilter = false,
  showDirectoryTagsMatch = false,
  showUndeliveredFilter = false,
  onUndeliveredFilterOn,
}: tLeadFiltersProps) {
  const { currentWorkspace, workspaceInfo } = useWorkspaceContext();
  const {
    openAdvanceFiltersModal,
    messageReadFilter,
    dateRange,
    dateFilterType,
    integrationFilter,
    statusFilter,
    tagsFilter,
    assignedUserFilters,
    currentFunnelStatus,
    activeFilterCount,
    arePipeFiltersActive: areFiltersSelected,
    setAdvanceSearchModalIsOpen,
    setOpenAdvanceFiltersModal,
    setMessageReadFilter,
    setDateRange,
    setDateFilterType,
    setIntegrationFilter,
    setStatusFilter,
    setTagsFilter,
    setAssignedUserFilters,
    undeliveredFilter,
    setUndeliveredFilter,
    resetFilters,
    directoryFunnelFilter,
    setDirectoryFunnelFilter,
    directoryStageFilter,
    setDirectoryStageFilter,
    directoryFunnels,
    directoryFilterCount,
    directoryTagsMatch,
    setDirectoryTagsMatch,
  } = useModalsContext();
  const { currentUserRole } = useUserContext();
  const { t } = useTranslation('navbar/navbar');

  // RET-2331: in the new Directory its funnel and stage count and clear with
  // the rest of the panel; everywhere else they are always off.
  const directoryCount = showDirectoryFunnelFilter ? directoryFilterCount : 0;
  const panelFilterCount = activeFilterCount + directoryCount;
  const canClearPanel = areFiltersSelected || directoryCount > 0;
  const funnelOptions = funnelFilterOptions(directoryFunnels, {
    all: t('filters.allFunnels'),
    inbox: t('filters.inbox'),
  });
  const stageOptions = stageFilterOptions({
    funnels: directoryFunnels,
    funnelId: directoryFunnelFilter,
    allLabel: t('filters.allStages'),
  });
  const canPickStage = canFilterByStage(directoryFunnels, directoryFunnelFilter);
  const { canUserAccessWhatsAppApi } = useACLContext();
  const { hasTiktokIntegration } = useIntegrationsContext();

  const [theme] = useLocalStorageState<string | undefined>('leadsales', {
    listenStorageChange: true,
  });
  const isDarkMode = theme !== 'leadsales';

  const startDate = dateRange?.[0];
  const endDate = dateRange?.[1];

  const [selectedTags, setSelectedTags] = useState<Array<tTagFilter>>(
    tagsFilter || [],
  );

  const [tagsData, setTagsData] = useState<{
    message: string;
    data: Array<tTagFilter>;
  } | null>(null);

  useEffect(() => {
    setSelectedTags(tagsFilter || []);
  }, [tagsFilter]);

  useEffect(() => {
    if (openAdvanceFiltersModal) {
      const fetchTags = async () => {
        if (currentWorkspace) {
          try {
            const response = await PipeService.getTags(currentWorkspace.id);
            if (!response || !response.data) {
              throw new Error(t('filters.tagsError'));
            }
            setTagsData(response);
          } catch (error) {
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'LeadFilters.component',
                operation: LOG_OPERATIONS.ERROR,
              },
              t('filters.tagsError'),
              error as unknown as Error,
            );
          }
        }
      };

      fetchTags();
    }
  }, [openAdvanceFiltersModal]);

  // Esc closes the panel. Bound only while it is open so this never competes
  // with other Escape handlers (chat composer, modals) when it is shut.
  useEffect(() => {
    if (!openAdvanceFiltersModal) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // A sub-popup owns this Escape. react-select (Etiquetas) and
      // react-datepicker (Fecha) both close on Escape and let the event bubble
      // to document, so dismissing the whole panel here would swallow the
      // user's intent: they meant to shut the tag list, not the filters.
      //
      // Checked by the popup's own markup rather than by blanket-stopping
      // propagation inside those fields, so Escape with nothing open still
      // closes the panel — which is what a user in an idle field expects.
      const hasOpenSubPopup =
        document.querySelector('.custom-select__menu') !== null ||
        document.querySelector('.react-datepicker') !== null;

      if (hasOpenSubPopup) return;

      setOpenAdvanceFiltersModal(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openAdvanceFiltersModal, setOpenAdvanceFiltersModal]);

  const leadAssignedUserDropdownTitle =
    assignedUserFilters?.name || t('filters.all');

  // Class names kept on the Navbar prefix: the styles they point at still live
  // in Navbar.styles.scss and are shared by both mounts.
  const NavbarClasses = {
    ShowFilter: 'Navbar__rightContainer__Button__ShowFilter',
    Default: 'Navbar__rightContainer__Button__Default',
    Filter: 'Navbar__rightContainer__Button__Filter',
  };

  const shouldShowAdvanceFiltersModal = showFilters;

  return (
    <div className="LeadFilters">
        {showSearch ? (
          <Button
            startIcon={<MagnifyingGlassIcon className="w-5" />}
            className={
              shouldShowAdvanceFiltersModal
                ? NavbarClasses.ShowFilter
                : NavbarClasses.Default
            }
            onClick={() => {
              setAdvanceSearchModalIsOpen(true);
            }}
          >
            <span className="flex-1 text-left">{t('searchButton')}</span>
          </Button>
        ) : null}
        {shouldShowAdvanceFiltersModal ? (
          <div className="LeadFilters__FilterGroup">
            {showFilterButton ? (
              <Button
                className={
                  areFiltersSelected
                    ? 'LeadFilters__FilterButton LeadFilters__FilterButton--active'
                    : `LeadFilters__FilterButton ${NavbarClasses.Filter}`
                }
                onClick={() => {
                  setOpenAdvanceFiltersModal((prevValue) => !prevValue);
                }}
              >
                <FunnelIcon className="w-5 text-primary" />
                <ActiveFilterBadge count={activeFilterCount} compact />
                {areFiltersSelected
                  ? t('filters.titleActive')
                  : t('filters.title')}
              </Button>
            ) : null}
            {/* Clearing required opening the drawer, finding "Limpiar filtros"
                and closing it again. Knowing you are filtered is only useful if
                undoing it is one click. A sibling rather than a child, because
                a <button> inside a <button> is invalid. */}
            {showFilterButton && areFiltersSelected ? (
              <Button
                className="LeadFilters__Clear"
                aria-label={t('filters.clearFilters')}
                title={t('filters.clearFilters')}
                onClick={resetFilters}
              >
                <XMarkIcon />
              </Button>
            ) : null}
            {/* Scrim: dims the board so the panel reads as the active layer,
                and gives the panel the click-outside dismiss it never had —
                until now the X was the only way to close it. Deliberately not
                a backdrop blur: this panel filters the board behind it, so
                that board must stay readable while the filters change, and
                blurring a scrolling kanban forces the whole area to
                recomposite on every scroll. */}
            {openAdvanceFiltersModal ? (
              <div
                className="LeadFilters__Scrim"
                aria-hidden="true"
                onClick={() => setOpenAdvanceFiltersModal(false)}
              />
            ) : null}
            {openAdvanceFiltersModal ? (
              <div
                className={`LeadFilters__Panel fixed w-80 max-w-[85vw] overflow-y-auto form-control p-0 gap-2 border border-base-200 ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
              >
                <div className="flex items-center justify-between p-3 pr-1 border-b">
                  <div className="flex items-center gap-2">
                    <h3 className="text-primary font-sans font-bold">
                      {t('filters.advancedFilters')}
                    </h3>
                    <ActiveFilterBadge count={panelFilterCount} />
                  </div>
                  <Button
                    shape="circle"
                    color="ghost"
                    className="LeadFilters__Close"
                    aria-label={t('filters.close')}
                    onClick={() => {
                      setOpenAdvanceFiltersModal(false);
                    }}
                  >
                    {/* Both dimensions: w-5 alone left the glyph collapsing to
                        a few pixels inside the button. */}
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
                <Button
                  className="mx-3 h-10 rounded input-bordered"
                  size="sm"
                  variant="outline"
                  disabled={!canClearPanel}
                  onClick={resetFilters}
                >
                  {t('filters.clearFilters')}
                </Button>
                {showDirectoryFunnelFilter ? (
                  <div className="form-control gap-2 px-3">
                    <label
                      className="label-text font-bold"
                      htmlFor="lead-filters-directory-funnel"
                    >
                      {t('filters.funnel')}
                    </label>
                    <DsSelect
                      id="lead-filters-directory-funnel"
                      size="sm"
                      value={directoryFunnelFilter}
                      onChange={(event) => {
                        // Another funnel's stages are not this one's.
                        setDirectoryFunnelFilter(event.target.value);
                        setDirectoryStageFilter(ALL_FILTER);
                      }}
                    >
                      {funnelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </DsSelect>
                    <label
                      className="label-text font-bold"
                      htmlFor="lead-filters-directory-stage"
                    >
                      {t('filters.stage')}
                    </label>
                    <DsSelect
                      id="lead-filters-directory-stage"
                      size="sm"
                      value={directoryStageFilter}
                      disabled={!canPickStage}
                      title={canPickStage ? undefined : t('filters.pickFunnelFirst')}
                      onChange={(event) => setDirectoryStageFilter(event.target.value)}
                    >
                      {stageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </DsSelect>
                    {canPickStage ? null : (
                      <p className="text-xs text-[var(--ls-text-neutral-light)]">
                        {t('filters.pickFunnelFirst')}
                      </p>
                    )}
                  </div>
                ) : null}
                {showUndeliveredFilter ? (
                  <label
                    className="LeadFilters__Undelivered mx-3"
                    data-testid="lead-filters-undelivered"
                  >
                    <Toggle
                      size="sm"
                      className="LeadFilters__Undelivered__Toggle"
                      checked={undeliveredFilter}
                      onChange={() => {
                        if (!undeliveredFilter) onUndeliveredFilterOn?.();
                        setUndeliveredFilter(!undeliveredFilter);
                      }}
                    />
                    <span>{t('filters.undeliveredOnly')}</span>
                  </label>
                ) : null}
                <div className="form-control gap-2 px-3">
                  <label className="label-text font-bold">
                    {t('filters.messages')}
                  </label>
                  <div className="flex gap-3">
                    <div
                      className="flex items-center gap-2"
                      onClick={() => {
                        setMessageReadFilter(undefined);
                      }}
                    >
                      <Radio
                        color="primary"
                        name="messages"
                        size="sm"
                        checked={!messageReadFilter}
                        readOnly
                      />
                      <label className="label-text-alt">
                        {t('filters.all')}
                      </label>
                    </div>
                    <div
                      className="flex items-center gap-2"
                      onClick={() => {
                        setMessageReadFilter('read');
                      }}
                    >
                      <Radio
                        color="primary"
                        name="messages"
                        size="sm"
                        checked={messageReadFilter === 'read'}
                        readOnly
                      />
                      <label className="label-text-alt">
                        {t('filters.read')}
                      </label>
                    </div>
                    <div
                      className="flex items-center gap-2"
                      onClick={() => {
                        setMessageReadFilter('unread');
                      }}
                    >
                      <Radio
                        color="primary"
                        name="messages"
                        size="sm"
                        value="archived"
                        checked={messageReadFilter === 'unread'}
                        readOnly
                      />
                      <label className="label-text-alt">
                        {t('filters.unread')}
                      </label>
                    </div>
                  </div>
                  <Divider className="m-0" />
                </div>
                <div className="form-control gap-2 px-3">
                  <label className="label-text font-bold">
                    {t('filters.date')}
                  </label>
                  <Dropdown>
                    <Dropdown.Toggle
                      button={false}
                      className={`input input-sm h-10 input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
                    >
                      <span className={dateFilterType ? '' : 'text-gray-400'}>
                        {dateFilterType === eDateFilterType.LAST_ACTION_AT
                          ? t('filters.lastActionAt')
                          : dateFilterType === eDateFilterType.CREATED_AT
                            ? t('filters.createdAt')
                            : t('filters.selectDateFilterType')}
                      </span>
                      <ChevronDownIcon className="w-4 ml-1" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-60 mt-0 z-30 max-h-[200px] overflow-y-auto overflow-x-hidden !block">
                      <Dropdown.Item
                        className="rounded-none hover:bg-primary hover:text-white"
                        onClick={() => {
                          setDateFilterType(eDateFilterType.CREATED_AT);
                          (document.activeElement as HTMLElement)?.blur();
                        }}
                      >
                        {t('filters.createdAt')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="rounded-none hover:bg-primary hover:text-white"
                        onClick={() => {
                          setDateFilterType(eDateFilterType.LAST_ACTION_AT);
                          (document.activeElement as HTMLElement)?.blur();
                        }}
                      >
                        {t('filters.lastActionAt')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <div
                    className={`input input-sm h-10 input-bordered flex items-center gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
                  >
                    <CalendarIcon className="w-5 h-5" />
                    <DatePicker
                      selectsRange
                      placeholderText={t('filters.selectADate')}
                      popperClassName="ReactDatepicker_Popper"
                      wrapperClassName="flex-1"
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={new Date()}
                      onChange={(update) => {
                        const [startDateUpdate, endDateUpdate] = update;
                        const startDateForFilter = startDateUpdate || undefined;
                        const endDateForFilters = endDateUpdate || undefined;

                        setDateRange([startDateForFilter, endDateForFilters]);
                      }}
                    >
                      <div className="flex w-full justify-center">
                        <Button
                          size="xs"
                          variant="link"
                          onClick={() => {
                            setDateRange([new Date(), new Date()]);
                          }}
                        >
                          {t('filters.todayLeads')}
                        </Button>
                      </div>
                    </DatePicker>
                    {(startDate || endDate) ? (
                      <XMarkIcon
                        className="w-4 cursor-pointer"
                        onClick={() => setDateRange([undefined, undefined])}
                      />
                    ) : (
                      <ChevronDownIcon className="w-4" />
                    )}
                  </div>
                  <label className="label p-0">
                    <span className="label-text-alt">
                      {dateFilterType === eDateFilterType.LAST_ACTION_AT
                        ? t('filters.selectLeadLastActionDate')
                        : dateFilterType === eDateFilterType.CREATED_AT
                          ? t('filters.selectLeadCreationDate')
                          : t('filters.selectDateFilterType')}
                    </span>
                  </label>
                  <Divider className="m-0" />
                </div>
                {/* Statuses */}
                <div className="form-control gap-2 px-3">
                  <label className="label-text font-bold">
                    {t('filters.status')}
                  </label>
                  <div className="form-control gap-3 grid grid-cols-2">
                    <div
                      className="flex items-center gap-1"
                      onClick={() => {
                        setStatusFilter(undefined);
                      }}
                    >
                      <Radio
                        color="primary"
                        name="statuses"
                        size="sm"
                        checked={!statusFilter}
                        readOnly
                      />
                      <label className="label-text-alt">
                        {t('filters.all')}
                      </label>
                    </div>
                    {Object.values(currentFunnelStatus ?? {}).map((status) => (
                      <div
                        className="flex items-center gap-1"
                        key={status.id}
                        onClick={() => {
                          setStatusFilter({
                            name: eLeadStatus[status.name as eLeadStatus],
                            uuid: status.id,
                          });
                        }}
                      >
                        <Radio
                          color="primary"
                          name="statuses"
                          size="sm"
                          checked={statusFilter?.name === status.name}
                          readOnly
                        />
                        <p>{showStatusEmoji(status.name)}</p>
                        <label className="label-text-alt">
                          {t('leadStatus.' + status.name.toLowerCase())}
                        </label>
                      </div>
                    ))}
                  </div>
                  {/* {currentUserRole !== eUserRole.SELLER ? (
                    <Divider className="m-0" />
                  ) : null} */}
                </div>

                <div className="form-control gap-2 px-3">
                  <label className="label-text font-bold">
                    {t('filters.integration')}
                  </label>
                  <div className="form-control gap-3">
                    <div
                      className="flex items-center gap-1"
                      onClick={() => {
                        setIntegrationFilter(undefined);
                      }}
                    >
                      <Radio
                        color="primary"
                        name="integrations"
                        size="sm"
                        checked={!integrationFilter}
                        readOnly
                      />
                      <label className="label-text-alt">
                        {t('filters.allFem')}
                      </label>
                    </div>
                    {canUserAccessWhatsAppApi() ? (
                      <div
                        className="flex items-center gap-1"
                        onClick={() => {
                          setIntegrationFilter(eChannelType.WHATSAPP_API);
                        }}
                      >
                        <Radio
                          color="primary"
                          name="integrations"
                          size="sm"
                          checked={
                            integrationFilter === eChannelType.WHATSAPP_API
                          }
                          readOnly
                        />
                        <img
                          src="/images/icons/whatsapp-business-icon.png"
                          className="w-4 h-4"
                        />
                        <label className="label-text-alt">WhatsApp Business API</label>
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-1"
                        onClick={() => {
                          setIntegrationFilter(eChannelType.WHATSAPP_WEB);
                        }}
                      >
                        <Radio
                          color="primary"
                          name="integrations"
                          size="sm"
                          checked={
                            integrationFilter === eChannelType.WHATSAPP_WEB
                          }
                          readOnly
                        />
                        <img
                          src="/images/icons/whatsapp-icon.png"
                          className="w-4 h-4"
                        />
                        <label className="label-text-alt">WhatsApp</label>
                      </div>
                    )}
                    <div
                      className="flex items-center gap-1"
                      onClick={() => {
                        setIntegrationFilter(eChannelType.MESSENGER);
                      }}
                    >
                      <Radio
                        color="primary"
                        name="integrations"
                        size="sm"
                        checked={integrationFilter === eChannelType.MESSENGER}
                        readOnly
                      />
                      <img
                        src="/images/icons/messenger-icon.png"
                        className="w-4 h-4"
                      />
                      <label className="label-text-alt">Messenger</label>
                    </div>
                    <div
                      className="flex items-center gap-1"
                      onClick={() => {
                        setIntegrationFilter(eChannelType.INSTAGRAM);
                      }}
                    >
                      <Radio
                        color="primary"
                        name="integrations"
                        size="sm"
                        checked={integrationFilter === eChannelType.INSTAGRAM}
                        readOnly
                      />
                      <img
                        src="/images/icons/instagram-icon.png"
                        className="w-4 h-4"
                      />
                      <label className="label-text-alt">Instagram</label>
                    </div>
                    {VITE_TIKTOK_FEATURE && hasTiktokIntegration() ? (
                      <div
                        className="flex items-center gap-1"
                        onClick={() => {
                          setIntegrationFilter(eChannelType.TIKTOK);
                        }}
                      >
                        <Radio
                          color="primary"
                          name="integrations"
                          size="sm"
                          checked={integrationFilter === eChannelType.TIKTOK}
                          readOnly
                        />
                        <img
                          src="/images/icons/tiktok-icon.png"
                          className="w-4 h-4"
                        />
                        <label className="label-text-alt">TikTok</label>
                      </div>
                    ) : null}
                  </div>
                  {currentUserRole !== eUserRole.SELLER ? (
                    <Divider className="m-0" />
                  ) : null}
                </div>

                {tagsData ? (
                  <div className="form-control gap-2 px-3">
                    <label className="label-text font-bold" id="tags-label">
                      {t('filters.tags')}
                    </label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="custom-select"
                      placeholder={t('filters.search')}
                      options={tagsData.data.map((tag) => ({
                        value: tag.id,
                        label: tag.name,
                      }))}
                      value={selectedTags.map((tag) => ({
                        value: tag.id,
                        label: tag.name,
                      }))}
                      onChange={(selectedOptions) => {
                        const updatedTags = selectedOptions.map((opt) => ({
                          id: opt.value,
                          name: opt.label,
                        }));
                        setSelectedTags(updatedTags);
                        setTagsFilter(updatedTags);
                      }}
                      closeMenuOnSelect={false}
                      styles={{
                        menuList: (base) => ({
                          ...base,
                          maxHeight: '150px',
                          overflowY: 'auto',
                        }),
                      }}
                    />
                    {showDirectoryTagsMatch && selectedTags.length > 1 ? (
                      <div
                        role="group"
                        aria-label={t('filters.tagsMatchLabel')}
                        className="flex flex-col gap-1"
                      >
                        <span className="text-xs text-[var(--ls-text-neutral-light)]">
                          {t('filters.tagsMatchLabel')}
                        </span>
                        <DsButtonGroup>
                          {(['any', 'all'] as const).map((mode) => (
                            <DsButton
                              key={mode}
                              type="button"
                              size="sm"
                              color={directoryTagsMatch === mode ? 'primary' : 'tertiary'}
                              aria-pressed={directoryTagsMatch === mode}
                              onClick={() => setDirectoryTagsMatch(mode)}
                            >
                              {mode === 'any'
                                ? t('filters.tagsMatchAny')
                                : t('filters.tagsMatchAll')}
                            </DsButton>
                          ))}
                        </DsButtonGroup>
                      </div>
                    ) : null}
                    {currentUserRole !== eUserRole.SELLER && (
                      <Divider className="m-0 mt-3" />
                    )}
                  </div>
                ) : (
                  <div className="no-content"></div>
                )}

                <div className="form-control gap-2 px-3 pb-3">
                  <label className="label-text font-bold">
                    {t('filters.users')}
                  </label>
                  <Dropdown>
                    <Dropdown.Toggle
                      button={false}
                      className={`input input-sm h-10 input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
                    >
                      {leadAssignedUserDropdownTitle}
                      <ChevronDownIcon className="w-4 ml-1" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-60 mt-0 z-30 max-h-[200px] overflow-y-auto overflow-x-hidden !block">
                      <Dropdown.Item
                        className="rounded-none hover:bg-primary hover:text-white"
                        onClick={() => {
                          setAssignedUserFilters(undefined);
                        }}
                      >
                        {t('filters.all')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="rounded-none hover:bg-primary hover:text-white"
                        onClick={() => {
                          setAssignedUserFilters({
                            name: t('filters.notAssigned'),
                            authId: 'NONE',
                          });
                        }}
                      >
                        {t('filters.notAssigned')}
                      </Dropdown.Item>
                      {Object.values(workspaceInfo?.users ?? {}).map((user) => (
                        <Dropdown.Item
                          className="rounded-none hover:bg-primary hover:text-white"
                          key={user.authId}
                          onClick={() => {
                            setAssignedUserFilters(user);
                          }}
                        >
                          {user.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

              </div>
            ) : null}
          </div>
        ) : null}
    </div>
  );
}
